import React, { useMemo, useState } from 'react';
import { AiPageContextSync } from '@/components/ai-agent/AiPageContextSync';
import { useAiListSelection } from '@/hooks/useAiListSelection';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Modal,
  Form,
  Select,
  InputNumber,
  Typography,
  Row,
  Col,
  Statistic,
  DatePicker,
  Steps,
  Progress,
  Divider,
  Descriptions,
  Spin,
  App,
  Alert,
  Tabs,
  Radio,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  DownloadOutlined,
  SearchOutlined,
  RocketOutlined,
  MinusCircleOutlined,
  ArrowLeftOutlined,
  SyncOutlined,
  ToolOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useWorkOrders,
  useWorkOrder,
  useReleaseWorkOrder,
  useStartWorkOrder,
  useCompleteWorkOrder,
  useReleaseAfterInspection,
  useUpdateWorkOrder,
  useWorkOrderProductionReceipts,
} from '../../hooks/useWorkOrder';
import { useUsers } from '../../hooks/useUsers';
import { useAuthStore } from '../../store/authStore';
import { isPlatformSuperAdmin } from '@/utils/authUser';
import workOrderService, {
  WORK_ORDER_DEVIATION_TYPES,
  WORK_ORDER_EXECUTION_SCOPES,
  type MaterialUtilizationLineInput,
  type WorkOrder as ApiWorkOrder,
  type ProductionReceiptRow,
  type WorkOrderBoqLineDocumentsLine,
} from '../../services/workOrder.service';
import assetService from '../../services/asset.service';
import { documentService } from '@/services/document.service';
import salesOrderService from '../../services/salesOrder.service';
import { useMaterialRequestsByWorkOrder } from '../../hooks/useMaterialRequest';
import { useStageInspections } from '../../hooks/useQCWorkflow';
import { useQualityPlanByWorkOrder } from '../../hooks/useQuality';
import { MaterialRequestDetailModal } from '../../components/manufacturing/MaterialRequestDetailModal';
import { CreateQualityPlanDialog } from '../../components/quality/CreateQualityPlanDialog';
import WorkOrderOperations from '../../components/manufacturing/WorkOrderOperations';
import { WorkOrderExecutionBadge } from '../../components/manufacturing/WorkOrderExecutionBadge';
import InitialInspectionRequestComponent from '../../components/qc/InitialInspectionRequest';
import FinalInspectionRequestComponent from '../../components/qc/FinalInspectionRequest';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import api from '@/services/api.service';
import { RelatedMocsPanel } from '@/components/moc/RelatedMocsPanel';
import { TaskRoutingCard } from '@/components/unified/TaskRoutingCard';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useCheckWorkOrderBlocked } from '../../hooks/useQCWorkflow';
import { iirService } from '../../services/qc-workflow.service';
const { Title, Text } = Typography;
const { Search } = Input;
const { TextArea } = Input;

/** Table + modal display row (mapped from API work order) */
interface WorkOrderDisplayRow {
  id: string;
  work_order_number: string;
  item_name?: string;
  bom_name: string;
  planned_qty: number;
  produced_qty: number;
  uom: string;
  status:
    | 'Draft'
    | 'Released'
    | 'In Progress'
    | 'Completed'
    | 'Cancelled'
    | 'Inspection Pending';
  planned_start_date: string;
  planned_end_date: string;
  actual_start_date?: string;
  actual_end_date?: string;
  project_name?: string;
  sales_order_number?: string;
  progress: number;
  created_at: string;
  execution_scope?: string;
}

function mapApiWorkOrderToDisplayRow(wo: ApiWorkOrder): WorkOrderDisplayRow {
  const plannedStart =
    wo.planned_start_date != null ? String(wo.planned_start_date) : '';
  const plannedEnd =
    wo.planned_end_date != null ? String(wo.planned_end_date) : '';
  return {
    ...wo,
    planned_qty: wo.planned_quantity,
    produced_qty: wo.produced_quantity,
    status: wo.work_order_status as WorkOrderDisplayRow['status'],
    bom_name: wo.bom_id || 'N/A',
    project_name: wo.project_name || wo.project_id || '',
    sales_order_number: wo.sales_order_number || wo.sales_order_id || '',
    progress:
      wo.planned_quantity > 0
        ? Math.round((wo.produced_quantity / wo.planned_quantity) * 100)
        : 0,
    planned_start_date: plannedStart,
    planned_end_date: plannedEnd,
    actual_start_date: wo.actual_start_date != null ? String(wo.actual_start_date) : undefined,
    actual_end_date: wo.actual_end_date != null ? String(wo.actual_end_date) : undefined,
    created_at: wo.created_at != null ? String(wo.created_at) : '',
  };
}

type MaterialUtilExecFormRow = {
  workOrderLineItemId?: string | null;
  drawingLineNo?: string;
  clientMaterialSpecification?: string;
  materialGrade?: string;
  headNo?: string;
  verification?: 'verified' | 'not_verified';
  remarks?: string;
};

function mapMatFormToPayload(rows: MaterialUtilExecFormRow[] | undefined): MaterialUtilizationLineInput[] {
  if (!rows?.length) return [];
  return rows.map((r) => ({
    workOrderLineItemId: (r.workOrderLineItemId ?? '').trim() || null,
    drawingLineNo: (r.drawingLineNo ?? '').trim(),
    clientMaterialSpecification: (r.clientMaterialSpecification ?? '').trim() || undefined,
    materialGrade: (r.materialGrade ?? '').trim() || undefined,
    headNo: (r.headNo ?? '').trim() || undefined,
    verified: r.verification === 'verified',
    notVerified: r.verification === 'not_verified',
    remarks: (r.remarks ?? '').trim() || undefined,
  }));
}

/** Stage inspection list row (API may include nested relations) */
type StageInspectionTableRow = {
  id: string;
  work_order_id: string;
  work_order_stage_id: string;
  inspection_number?: string;
  status?: string;
  result?: string;
  inspection_date?: string;
  workOrderStage?: { stage_code?: string; stage_name?: string };
  inspector?: { name?: string; email?: string };
};

const WorkOrdersPage: React.FC = () => {
  const { formatCurrency } = useCurrency();
  const { modal, message } = App.useApp();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const authUser = useAuthStore((s) => s.user);
  const canIssueMaterials =
    isPlatformSuperAdmin(authUser) ||
    authUser?.permissions?.includes('inventory.material_issue.create');
  const location = useLocation();
  const { workOrderId: workOrderIdParam } = useParams<{ workOrderId?: string }>();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrderDisplayRow | null>(null);
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState<string | null>(null);
  const [isMaterialRequestDetailVisible, setIsMaterialRequestDetailVisible] = useState(false);
  const [selectedMaterialRequestId, setSelectedMaterialRequestId] = useState<string | null>(null);
  const [isQualityPlanDialogVisible, setIsQualityPlanDialogVisible] = useState(false);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [activeTab, setActiveTab] = useState('iir');
  const [syncingDocs, setSyncingDocs] = useState(false);
  const [assetModalOpen, setAssetModalOpen] = useState(false);
  const [assetForm] = Form.useForm();
  const [assetSubmitting, setAssetSubmitting] = useState(false);
  const assetMovementType = Form.useWatch('movement_type', assetForm) as 'issue' | 'return' | 'damage' | undefined;

  // Fetch real work orders from API
  const { data: workOrders = [], isLoading, error } = useWorkOrders();
  const { data: workOrderDetails, isLoading: isLoadingDetails } = useWorkOrder(selectedWorkOrderId || '');
  const { data: revenueServiceTypesWo = [] } = useQuery({
    queryKey: ['sales-revenue-service-types', 'active'],
    queryFn: async () => {
      const res = await api.get('/sales/revenue-service-types?only_active=true');
      const body = (res as { data?: unknown })?.data ?? res;
      const list = Array.isArray(body) ? body : Array.isArray((body as { data?: unknown })?.data) ? (body as { data: { id: string; service_code: string; service_name: string }[] }).data : [];
      return list as { id: string; service_code: string; service_name: string }[];
    },
  });
  const woRstLabel = useMemo(() => {
    const rid = workOrderDetails?.revenue_service_type_id;
    if (!rid) return '—';
    const r = revenueServiceTypesWo.find((x) => x.id === rid);
    return r ? `${r.service_code} — ${r.service_name}` : '—';
  }, [workOrderDetails?.revenue_service_type_id, revenueServiceTypesWo]);

  const workOrderBreadcrumbSegments = useMemo(() => {
    if (!workOrderIdParam) return null;
    const label =
      (workOrderDetails?.work_order_number && String(workOrderDetails.work_order_number).trim()) ||
      workOrderIdParam;
    return [
      { title: 'Work Orders', path: '/mfg/work-orders' },
      { title: label, path: `/mfg/work-orders/${workOrderIdParam}` },
    ];
  }, [workOrderIdParam, workOrderDetails?.work_order_number]);
  useDocumentBreadcrumb(workOrderBreadcrumbSegments);
  const { data: fgReceipts = [], isLoading: loadingFgReceipts } = useWorkOrderProductionReceipts(
    selectedWorkOrderId || ''
  );
  const { data: woBoqOperations } = useQuery({
    queryKey: ['work-orders', selectedWorkOrderId, 'boq-operations'],
    queryFn: () => workOrderService.getBoqOperations(selectedWorkOrderId!),
    enabled: !!selectedWorkOrderId,
    staleTime: 60_000,
  });

  const { user } = useAuthStore();
  const canSeeWoBoqLineDocs = Boolean(user?.permissions?.includes('manufacturing.view'));

  const { data: woBoqLineDocuments } = useQuery({
    queryKey: ['work-orders', selectedWorkOrderId, 'boq-line-documents'],
    queryFn: () => workOrderService.getBoqLineDocuments(selectedWorkOrderId!),
    enabled: !!selectedWorkOrderId && canSeeWoBoqLineDocs,
    staleTime: 60_000,
  });

  const boqLineDocsByWoLineId = useMemo(() => {
    const m = new Map<string, WorkOrderBoqLineDocumentsLine>();
    for (const row of woBoqLineDocuments?.lines ?? []) {
      m.set(row.work_order_line_item_id, row);
    }
    return m;
  }, [woBoqLineDocuments]);

  const materialRequestContextPrefix = useMemo(() => {
    const parts: string[] = [];
    if (workOrderDetails?.work_order_number) {
      parts.push(`WO: ${workOrderDetails.work_order_number}`);
    }
    if (workOrderDetails?.sales_order_number) {
      parts.push(`SO: ${workOrderDetails.sales_order_number}`);
    }
    if (woBoqOperations?.boq_id) {
      parts.push(`BOQ: ${woBoqOperations.boq_id}`);
    }
    return parts.length ? parts.join(' | ') : undefined;
  }, [
    workOrderDetails?.work_order_number,
    workOrderDetails?.sales_order_number,
    woBoqOperations?.boq_id,
  ]);

  /** work_order_line_item_id → summary of BOQ operations (in-house vs outsourced) */
  const lineItemOpsSourceSummary = useMemo(() => {
    const map = new Map<string, React.ReactNode>();
    if (!woBoqOperations?.has_boq_chain || !woBoqOperations.lines?.length) return map;
    for (const line of woBoqOperations.lines) {
      const ops = line.operations ?? [];
      if (ops.length === 0) {
        map.set(line.work_order_line_item_id, <Text type="secondary">—</Text>);
        continue;
      }
      const outsourced = ops.filter((o) => o.is_outsourced).length;
      const inHouse = ops.length - outsourced;
      if (outsourced === 0) {
        map.set(line.work_order_line_item_id, <Tag color="blue">In-house</Tag>);
      } else if (inHouse === 0) {
        map.set(line.work_order_line_item_id, <Tag color="orange">Outsourced</Tag>);
      } else {
        map.set(
          line.work_order_line_item_id,
          <Space size={[4, 4]} wrap>
            <Tag color="blue">
              {inHouse} in-house
            </Tag>
            <Tag color="orange">
              {outsourced} outsourced
            </Tag>
          </Space>
        );
      }
    }
    return map;
  }, [woBoqOperations]);

  const updateWorkOrderMutation = useUpdateWorkOrder();
  const { data: activeUsers = [], isLoading: usersLoading } = useUsers({
    company_id: user?.company_id,
    is_active: true,
  });
  const [execEditOpen, setExecEditOpen] = useState(false);
  const [execForm] = Form.useForm();
  
  // ✅ Conditional queries - only load data for active tab
  const { data: materialRequests = [], isLoading: isLoadingMaterialRequests } = useMaterialRequestsByWorkOrder(
    selectedWorkOrderId || '', 
    { enabled: !!selectedWorkOrderId && activeTab === 'materialRequests' }
  );
  const { data: inspections = [], isLoading: isLoadingInspections } = useStageInspections(
    activeTab === 'inspections' && selectedWorkOrderId ? selectedWorkOrderId : '',
  );
  const { data: qualityPlan, isLoading: isLoadingQualityPlan } = useQualityPlanByWorkOrder(
    selectedWorkOrderId || '',
    { enabled: !!selectedWorkOrderId && activeTab === 'qualityPlan' }
  );
  
  // IIR must be approved before starting stages/operations and before requesting materials
  const { data: isBlockedData } = useCheckWorkOrderBlocked(
    selectedWorkOrderId || '',
    {
      enabled:
        !!selectedWorkOrderId &&
        (activeTab === 'operations' || activeTab === 'iir' || activeTab === 'materialRequests'),
    }
  );
  const isBlocked = isBlockedData?.isBlocked || false;

  const woSalesOrderId = workOrderDetails?.sales_order_id;
  const { data: soAmendments = [], isLoading: loadingSoAmendments } = useQuery({
    queryKey: ['sales-order-amendments', woSalesOrderId],
    queryFn: () => salesOrderService.listAmendments(woSalesOrderId!),
    enabled: Boolean(woSalesOrderId && selectedWorkOrderId && workOrderIdParam),
  });

  const { data: assetMovements = [], isLoading: loadingAssetMovements } = useQuery({
    queryKey: ['work-order-asset-movements', selectedWorkOrderId],
    queryFn: () => workOrderService.getAssetMovements(selectedWorkOrderId!),
    enabled: Boolean(selectedWorkOrderId && workOrderIdParam && activeTab === 'assets'),
  });
  const { data: availableAssets = [], isLoading: loadingAvailableAssets } = useQuery({
    queryKey: ['asset-master-options'],
    queryFn: () => assetService.list({ status: 'active' }),
    enabled: Boolean(selectedWorkOrderId && workOrderIdParam && activeTab === 'assets'),
  });
  const releaseWorkOrder = useReleaseWorkOrder();
  const startWorkOrder = useStartWorkOrder();
  const completeWorkOrder = useCompleteWorkOrder();
  const releaseAfterInspection = useReleaseAfterInspection();

  // Deep-link: legacy navigation with state.workOrderId → full-page detail URL
  React.useEffect(() => {
    const workOrderId = (location.state as { workOrderId?: string })?.workOrderId;
    if (!workOrderId) return;
    navigate(`/mfg/work-orders/${workOrderId}`, { replace: true, state: {} });
  }, [location.state, navigate]);

  React.useEffect(() => {
    if (!workOrderIdParam) {
      setSelectedWorkOrderId(null);
      setActiveTab('iir');
      return;
    }
    setSelectedWorkOrderId(workOrderIdParam);
    const wo = workOrders.find((w) => w.id === workOrderIdParam);
    if (wo) setSelectedWorkOrder(mapApiWorkOrderToDisplayRow(wo));
  }, [workOrderIdParam, workOrders]);

  const goBackToWorkOrderList = () => {
    navigate('/mfg/work-orders');
    setSelectedWorkOrderId(null);
    setActiveTab('iir');
  };

  // Mock data (for reference - now using real data)
  const mockWorkOrders_UNUSED: WorkOrderDisplayRow[] = [
    {
      id: '1',
      work_order_number: 'WO-2025-001',
      item_name: 'Aluminium Window Frame',
      bom_name: 'BOM-001 - Standard Frame',
      planned_qty: 50,
      produced_qty: 35,
      uom: 'PCS',
      status: 'In Progress',
      planned_start_date: '2025-12-20',
      planned_end_date: '2025-12-30',
      actual_start_date: '2025-12-20',
      project_name: 'Villa Construction - Phase 2',
      sales_order_number: 'SO-2025-045',
      progress: 70,
      created_at: '2025-12-15',
    },
    {
      id: '2',
      work_order_number: 'WO-2025-002',
      item_name: 'Steel Door Frame',
      bom_name: 'BOM-005 - Heavy Duty Door',
      planned_qty: 30,
      produced_qty: 0,
      uom: 'PCS',
      status: 'Released',
      planned_start_date: '2025-12-28',
      planned_end_date: '2026-01-10',
      progress: 0,
      created_at: '2025-12-18',
    },
  ];

  const formatWorkOrderStatusLabel = (status: string) =>
    status === 'IIR Pending' ? 'Job Inspection Pending' : status;

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'Draft': 'default',
      'Released': 'cyan',
      'In Progress': 'processing',
      'Completed': 'success',
      'Inspection Pending': 'warning',
      'IIR Pending': 'warning',
      'Cancelled': 'error',
    };
    return colors[status] || 'default';
  };

  const getInspectionStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'Pending': 'default',
      'Scheduled': 'default',
      'In Progress': 'processing',
      'Completed': 'success',
      'On Hold': 'warning',
      'Cancelled': 'error',
      'Failed': 'error',
    };
    return colors[status] || 'default';
  };

  const getResultColor = (result: string) => {
    const colors: Record<string, string> = {
      'Pass': 'success',
      'Fail': 'error',
      'Conditional': 'warning',
      'Pending': 'default',
      'N/A': 'default',
    };
    return colors[result] || 'default';
  };

  const handleCreate = () => {
    form.resetFields();
    setSelectedWorkOrder(null);
    setIsModalVisible(true);
  };

  const handleView = (record: WorkOrderDisplayRow) => {
    setSelectedWorkOrder(record);
    navigate(`/mfg/work-orders/${record.id}`);
  };

  const handleStatusChange = async (workOrder: WorkOrderDisplayRow, action: string) => {
    // Check if work order is blocked by IIR
    if (action === 'Start' || action === 'Complete') {
      try {
        const { data: blockedData } = await iirService.checkWorkOrderBlocked(workOrder.id);
        if (blockedData?.isBlocked) {
          modal.warning({
            title: 'Operations Blocked',
            content: 'Work order operations are blocked. Job Inspection must be approved before starting or completing work.',
          });
          return;
        }
      } catch (error) {
        console.error('Error checking work order block status:', error);
      }
    }

    modal.confirm({
      title: `${action} Work Order`,
      content:
        action === 'Complete'
          ? `Complete work order ${workOrder.work_order_number}? If every operation has stage inspection turned off, the work order goes to Ready for Dispatch (Final Inspection is skipped). Otherwise a Final Inspection Request is created.`
          : `Are you sure you want to ${action.toLowerCase()} work order ${workOrder.work_order_number}?`,
      onOk: async () => {
        try {
          if (action === 'Release') {
            await releaseWorkOrder.mutateAsync(workOrder.id);
          } else if (action === 'Start') {
            await startWorkOrder.mutateAsync(workOrder.id);
          } else if (action === 'Complete') {
            await completeWorkOrder.mutateAsync(workOrder.id);
          } else if (action === 'Release from Inspection') {
            await releaseAfterInspection.mutateAsync(workOrder.id);
          }
        } catch (error) {
          // Error handling is done in the hooks
          console.error(`Error ${action.toLowerCase()}ing work order:`, error);
        }
      },
    });
  };

  const handleSubmit = async (values: any) => {
    try {
      console.log('Work Order Data:', values);
      message.success('Work order created successfully');
      setIsModalVisible(false);
    } catch (error) {
      message.error('Error creating work order');
    }
  };

  const columns: ColumnsType<WorkOrderDisplayRow> = [
    {
      title: 'WO Number',
      dataIndex: 'work_order_number',
      key: 'work_order_number',
      fixed: 'left',
      width: 140,
      render: (text, record) => (
        <Space direction="vertical" size={2}>
          <Text strong>{text}</Text>
          {record.execution_scope ? (
            <WorkOrderExecutionBadge executionScope={record.execution_scope} size="compact" />
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Item',
      key: 'item',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.item_name}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.bom_name}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Quantity',
      key: 'quantity',
      width: 120,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{`${record.produced_qty} / ${record.planned_qty} ${record.uom}`}</Text>
          <Progress
            percent={Math.round((record.produced_qty / record.planned_qty) * 100)}
            size="small"
            status={record.status === 'Completed' ? 'success' : 'active'}
          />
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={getStatusColor(status)}>{formatWorkOrderStatusLabel(status)}</Tag>
      ),
      filters: [
        { text: 'Draft', value: 'Draft' },
        { text: 'Released', value: 'Released' },
        { text: 'In Progress', value: 'In Progress' },
        { text: 'Completed', value: 'Completed' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Planned Dates',
      key: 'dates',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: '12px' }}>
            Start: {dayjs(record.planned_start_date).format('DD MMM YYYY')}
          </Text>
          <Text style={{ fontSize: '12px' }}>
            End: {dayjs(record.planned_end_date).format('DD MMM YYYY')}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Project',
      dataIndex: 'project_name',
      key: 'project_name',
      width: 180,
      render: (text) => text || <Text type="secondary">-</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 250,
      render: (_, record) => (
        <Space wrap>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          >
            View
          </Button>
          {record.status === 'Draft' && (
            <Button
              type="link"
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => handleStatusChange(record, 'Release')}
            >
              Release
            </Button>
          )}
          {record.status === 'Released' && (
            <Button
              type="link"
              size="small"
              icon={<RocketOutlined />}
              onClick={() => handleStatusChange(record, 'Start')}
              disabled={isBlocked}
              title={isBlocked ? 'Job Inspection must be approved before starting work order' : ''}
            >
              Start
            </Button>
          )}
          {record.status === 'In Progress' && (
            <Button
              type="link"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => handleStatusChange(record, 'Complete')}
              disabled={isBlocked}
              title={isBlocked ? 'Job Inspection must be approved before completing work order' : ''}
            >
              Complete
            </Button>
          )}
          {record.status === 'Inspection Pending' && (
            <Button
              type="link"
              size="small"
              icon={<RocketOutlined />}
              onClick={() => handleStatusChange(record, 'Release from Inspection')}
            >
              Release
            </Button>
          )}
          {(record.status === 'Draft' || record.status === 'Released') && (
            <Button
              type="link"
              danger
              size="small"
              icon={<CloseCircleOutlined />}
              onClick={() => handleStatusChange(record, 'Cancel')}
            >
              Cancel
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const displayWorkOrders = workOrders.map(mapApiWorkOrderToDisplayRow);

  const filteredWorkOrders = displayWorkOrders.filter((wo) => {
    const q = searchText.trim().toLowerCase();
    const matchesSearch =
      !q ||
      wo.work_order_number.toLowerCase().includes(q) ||
      (wo.item_name && wo.item_name.toLowerCase().includes(q)) ||
      (wo.project_name && String(wo.project_name).toLowerCase().includes(q));
    const matchesStatus = statusFilter === '' || wo.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const { rowSelection } = useAiListSelection({
    entityType: 'WorkOrder',
    module: 'manufacturing',
    listFilters: { search: searchText || undefined, status: statusFilter || undefined },
    labelById: (id) => filteredWorkOrders.find((w) => w.id === id)?.work_order_number,
  });

  const stats = {
    total: displayWorkOrders.length,
    inProgress: displayWorkOrders.filter(wo => wo.status === 'In Progress').length,
    completed: displayWorkOrders.filter(wo => wo.status === 'Completed').length,
    avgCompletion: displayWorkOrders.length > 0 
      ? Math.round(displayWorkOrders.reduce((sum, wo) => sum + wo.progress, 0) / displayWorkOrders.length)
      : 0,
  };

  if (isLoading) {
    return (
      <div
        style={{
          paddingTop: 100,
          paddingLeft: 24,
          paddingRight: 24,
          paddingBottom: 24,
          textAlign: 'center',
        }}
      >
      {workOrderIdParam ? <AiPageContextSync module="manufacturing" docType="WorkOrder" docId={workOrderIdParam} /> : null}
        <Spin size="large" tip="Loading work orders...">
          <div style={{ minHeight: 80 }} />
        </Spin>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Card>
          <Text type="danger">Error loading work orders: {(error as Error).message}</Text>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {workOrderIdParam ? <AiPageContextSync module="manufacturing" docType="WorkOrder" docId={workOrderIdParam} /> : null}
      {!workOrderIdParam && (
        <>
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={24}>
              <Title level={2}>
                <RocketOutlined /> Work Orders
              </Title>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card>
                <Statistic title="Total Work Orders" value={stats.total} />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="In Progress"
                  value={stats.inProgress}
                  valueStyle={{ color: '#145A54' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Completed"
                  value={stats.completed}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Avg Completion"
                  value={stats.avgCompletion}
                  suffix="%"
                />
              </Card>
            </Col>
          </Row>

          <Card>
            <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
              <Space>
                <Search
                  placeholder="Search WO #, item, project..."
                  allowClear
                  style={{ width: 300 }}
                  onChange={(e) => setSearchText(e.target.value)}
                  prefix={<SearchOutlined />}
                />
                <Select
                  placeholder="Filter by status"
                  allowClear
                  style={{ width: 150 }}
                  onChange={(value) => setStatusFilter(value || '')}
                >
                  <Select.Option value="Draft">Draft</Select.Option>
                  <Select.Option value="Released">Released</Select.Option>
                  <Select.Option value="In Progress">In Progress</Select.Option>
                  <Select.Option value="Completed">Completed</Select.Option>
                </Select>
              </Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                Create Work Order
              </Button>
            </Space>

            <Table
              columns={columns}
              dataSource={filteredWorkOrders}
              rowKey="id"
              rowSelection={rowSelection}
              scroll={{ x: 1400 }}
              pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
            />
          </Card>
        </>
      )}

      {/* Create Modal */}
      <Modal
        title="Create Work Order"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        width={800}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Item"
                name="item_id"
                rules={[{ required: true, message: 'Please select item' }]}
              >
                <Select placeholder="Select item to manufacture">
                  <Select.Option value="item-1">Aluminium Window Frame</Select.Option>
                  <Select.Option value="item-2">Steel Door Frame</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="BOM"
                name="bom_id"
                rules={[{ required: true, message: 'Please select BOM' }]}
              >
                <Select placeholder="Select BOM">
                  <Select.Option value="bom-1">BOM-001 - Standard Frame</Select.Option>
                  <Select.Option value="bom-2">BOM-002 - Premium Frame</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Planned Quantity"
                name="planned_qty"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="UOM"
                name="uom"
                initialValue="PCS"
              >
                <Select>
                  <Select.Option value="PCS">PCS</Select.Option>
                  <Select.Option value="SET">SET</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Project" name="project_id">
                <Select placeholder="Optional" allowClear>
                  <Select.Option value="proj-1">Villa Construction - Phase 2</Select.Option>
                  <Select.Option value="proj-2">Office Building</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Planned Start Date"
                name="planned_start_date"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Planned End Date"
                name="planned_end_date"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Remarks" name="remarks">
            <TextArea rows={3} placeholder="Optional remarks..." />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setIsModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">Create</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {workOrderIdParam && (
        <Card
          title={
            <Space>
              <Button type="text" icon={<ArrowLeftOutlined />} onClick={goBackToWorkOrderList}>
                Back to list
              </Button>
              <Text strong>Work order details</Text>
            </Space>
          }
          extra={
            workOrderDetails ? (
            <Space wrap>
              <Button
                icon={<PrinterOutlined />}
                onClick={() => navigate(`/mfg/work-orders/${workOrderDetails.id}/print`)}
              >
                Print
              </Button>
              <Button
                icon={<PrinterOutlined />}
                onClick={() => navigate(`/mfg/work-orders/${workOrderDetails.id}/route-sheet`)}
              >
                Print route sheet
              </Button>
              <Button
                icon={<PrinterOutlined />}
                onClick={() =>
                  navigate(`/mfg/work-orders/${workOrderDetails.id}/route-sheet?view=operator`)
                }
              >
                Print operator sheets
              </Button>
              <Button
                icon={<PrinterOutlined />}
                onClick={() =>
                  navigate(`/mfg/work-orders/${workOrderDetails.id}/route-sheet?view=line-in-process`)
                }
              >
                Print line in-process
              </Button>
            {!['Cancelled', 'Closed'].includes(workOrderDetails.work_order_status) ? (
              <Button
                icon={<EditOutlined />}
                onClick={() => {
                  if (!workOrderDetails) return;
                  execForm.setFieldsValue({
                    executionScope: workOrderDetails.execution_scope,
                    deviationType: workOrderDetails.deviation_type,
                    clientScopeOfWork: workOrderDetails.client_scope_of_work,
                    clientQualityRequirements: workOrderDetails.client_quality_requirements,
                    wo_customer_requirements_notes: workOrderDetails.wo_customer_requirements_notes ?? '',
                    productionSupervisorUserId: workOrderDetails.production_supervisor_user_id,
                    qualityEngineerUserId: workOrderDetails.quality_engineer_user_id,
                    matLines:
                      workOrderDetails.materialUtilizationLines?.map((l) => ({
                        workOrderLineItemId: l.work_order_line_item_id || undefined,
                        drawingLineNo: l.drawing_line_no,
                        clientMaterialSpecification: l.client_material_specification,
                        materialGrade: l.material_grade,
                        headNo: l.head_no,
                        verification: l.verified
                          ? ('verified' as const)
                          : l.not_verified
                            ? ('not_verified' as const)
                            : undefined,
                        remarks: l.remarks,
                      })) || [],
                  });
                  setExecEditOpen(true);
                }}
              >
                Edit execution and materials
              </Button>
            ) : null}
            </Space>
            ) : null
          }
        >
        {isLoadingDetails ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        ) : workOrderDetails && (
          <>
            <TaskRoutingCard
              sourceDocumentType="WorkOrder"
              sourceDocumentId={workOrderDetails.id}
              nextActionUserId={workOrderDetails.next_action_user_id}
              documentOwnerLabel="Production supervisor"
              documentOwnerUserId={workOrderDetails.production_supervisor_user_id}
              disabled={['Completed', 'Closed', 'Cancelled'].includes(workOrderDetails.work_order_status)}
              invalidateQueryKeys={[['work-orders', workOrderDetails.id]]}
            />
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="WO Number" span={2}>
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <Text strong>{workOrderDetails.work_order_number}</Text>
                  <WorkOrderExecutionBadge executionScope={workOrderDetails.execution_scope} />
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(workOrderDetails.work_order_status)}>
                  {formatWorkOrderStatusLabel(workOrderDetails.work_order_status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Progress">
                <Progress 
                  percent={Math.round((Number(workOrderDetails.produced_quantity) / Number(workOrderDetails.planned_quantity)) * 100)} 
                  size="small" 
                />
              </Descriptions.Item>
              <Descriptions.Item label="Item">{workOrderDetails.item_name || 'Multiple Items'}</Descriptions.Item>
              <Descriptions.Item label="BOM">{workOrderDetails.bom_id || 'N/A'}</Descriptions.Item>
              <Descriptions.Item label="Planned Qty">
                {workOrderDetails.planned_quantity} {workOrderDetails.uom}
              </Descriptions.Item>
              <Descriptions.Item label="Produced Qty">
                {workOrderDetails.produced_quantity} {workOrderDetails.uom}
              </Descriptions.Item>
              <Descriptions.Item label="Project">{workOrderDetails.project_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="Customer Order">{workOrderDetails.sales_order_number || '-'}</Descriptions.Item>
              <Descriptions.Item label="Revenue service type">{woRstLabel}</Descriptions.Item>
              <Descriptions.Item label="Production request">
                {workOrderDetails.source_document_type === 'production_request' &&
                workOrderDetails.source_document_id ? (
                  <Link to={`/mfg/production-requests/${workOrderDetails.source_document_id}`}>
                    {workOrderDetails.production_request_number || workOrderDetails.source_document_id}
                  </Link>
                ) : (
                  workOrderDetails.production_request_number || '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Planned Start">
                {workOrderDetails.planned_start_date ? dayjs(workOrderDetails.planned_start_date).format('DD MMM YYYY') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Planned End">
                {workOrderDetails.planned_end_date ? dayjs(workOrderDetails.planned_end_date).format('DD MMM YYYY') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Execution scope">
                {workOrderDetails.execution_scope || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Deviation">{workOrderDetails.deviation_type || '—'}</Descriptions.Item>
              <Descriptions.Item label="Scope of work (client)" span={2}>
                {workOrderDetails.client_scope_of_work || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Quality requirements (client)" span={2}>
                {workOrderDetails.client_quality_requirements || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="WO customer requirements notes" span={2}>
                {workOrderDetails.wo_customer_requirements_notes || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Production supervisor">
                {workOrderDetails.production_supervisor
                  ? `${workOrderDetails.production_supervisor.first_name} ${workOrderDetails.production_supervisor.last_name || ''}`.trim()
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Quality engineer">
                {workOrderDetails.quality_engineer
                  ? `${workOrderDetails.quality_engineer.first_name} ${workOrderDetails.quality_engineer.last_name || ''}`.trim()
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Total estimated hours">
                {workOrderDetails.total_estimated_hours != null
                  ? `${Number(workOrderDetails.total_estimated_hours).toFixed(2)} h`
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Total actual hours">
                {workOrderDetails.total_actual_hours != null
                  ? `${Number(workOrderDetails.total_actual_hours).toFixed(2)} h`
                  : '—'}
              </Descriptions.Item>
            </Descriptions>

            {workOrderIdParam ? <RelatedMocsPanel workOrderId={workOrderIdParam} /> : null}

            {workOrderDetails.materialUtilizationLines &&
              workOrderDetails.materialUtilizationLines.length > 0 && (
                <>
                  <Divider orientation="left">Material utilization</Divider>
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="id"
                    dataSource={[...workOrderDetails.materialUtilizationLines].sort(
                      (a, b) => a.line_number - b.line_number
                    )}
                    columns={[
                      { title: 'S.No', dataIndex: 'line_number', width: 56 },
                      { title: 'Drawing line no.', dataIndex: 'drawing_line_no', width: 120 },
                      {
                        title: 'Client mat. spec.',
                        dataIndex: 'client_material_specification',
                        ellipsis: true,
                      },
                      { title: 'Grade', dataIndex: 'material_grade', width: 100 },
                      { title: 'Heat no.', dataIndex: 'head_no', width: 90 },
                      {
                        title: 'BOQ line',
                        width: 90,
                        render: (_: unknown, r: NonNullable<ApiWorkOrder['materialUtilizationLines']>[0]) => {
                          const li = workOrderDetails.lineItems?.find((x) => x.id === r.work_order_line_item_id);
                          return li ? `L${li.line_number}` : '—';
                        },
                      },
                      {
                        title: 'Verified',
                        width: 80,
                        render: (_: unknown, r: NonNullable<ApiWorkOrder['materialUtilizationLines']>[0]) =>
                          r.verified ? <Tag color="success">Yes</Tag> : <Tag>No</Tag>,
                      },
                      {
                        title: 'Not verified',
                        width: 100,
                        render: (_: unknown, r: NonNullable<ApiWorkOrder['materialUtilizationLines']>[0]) =>
                          r.not_verified ? <Tag color="warning">Yes</Tag> : <Tag>No</Tag>,
                      },
                      { title: 'Remarks', dataIndex: 'remarks', ellipsis: true },
                    ]}
                  />
                </>
              )}

            <Divider>Production Timeline</Divider>
            <Steps
              current={workOrderDetails.work_order_status === 'Draft' ? 0 :
                      workOrderDetails.work_order_status === 'Released' ? 1 :
                      workOrderDetails.work_order_status === 'In Progress' ? 2 : 3}
              items={[
                { title: 'Draft', description: 'Work order created' },
                { title: 'Released', description: 'Ready for production' },
                { title: 'In Progress', description: 'Manufacturing' },
                { title: 'Completed', description: 'Finished goods received' },
              ]}
            />

            <Divider />

            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              items={[
                {
                  key: 'materialRequests',
                  label: `Material Requests (${materialRequests.length})`,
                  children: (
                    <div>
                      <div style={{ marginBottom: 16 }}>
                        <Tooltip
                          title={
                            isBlocked
                              ? 'Job Inspection must be approved before requesting materials. Open the Job Inspection tab to review status.'
                              : undefined
                          }
                        >
                          <span>
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              disabled={isBlocked}
                              onClick={() => {
                                if (!selectedWorkOrderId) return;
                                const params = new URLSearchParams({
                                  workOrderId: selectedWorkOrderId,
                                  returnTo: `/mfg/work-orders/${selectedWorkOrderId}`,
                                });
                                if (materialRequestContextPrefix) {
                                  params.set('context', materialRequestContextPrefix);
                                }
                                navigate(`/scm/inventory/material-requests/new?${params.toString()}`);
                              }}
                            >
                              Request Materials
                            </Button>
                          </span>
                        </Tooltip>
                      </div>
                      {isLoadingMaterialRequests ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                          <Spin />
                        </div>
                      ) : materialRequests.length > 0 ? (
                        <Table
                          dataSource={materialRequests}
                          rowKey="id"
                          pagination={false}
                          size="small"
                          columns={[
                            {
                              title: 'Request #',
                              dataIndex: 'request_number',
                              width: 120,
                            },
                            {
                              title: 'Type',
                              dataIndex: 'request_type',
                              width: 80,
                              render: (type: string) => (
                                <Tag color={type === 'BOM' ? 'blue' : type === 'Tools' ? 'orange' : 'green'}>
                                  {type}
                                </Tag>
                              ),
                            },
                            {
                              title: 'Status',
                              dataIndex: 'request_status',
                              width: 120,
                              render: (status: string) => {
                                const colorMap: Record<string, string> = {
                                  'Draft': 'default',
                                  'Pending': 'warning',
                                  'Approved': 'success',
                                  'Rejected': 'error',
                                  'Partially Issued': 'processing',
                                  'Fully Issued': 'success',
                                  'Closed': 'default',
                                  'Cancelled': 'error',
                                };
                                return <Tag color={colorMap[status]}>{status}</Tag>;
                              },
                            },
                            {
                              title: 'Approval',
                              dataIndex: 'approval_status',
                              width: 120,
                              render: (status: string) => {
                                const colorMap: Record<string, string> = {
                                  'Not Required': 'default',
                                  'Pending': 'warning',
                                  'Approved': 'success',
                                  'Rejected': 'error',
                                };
                                return <Tag color={colorMap[status]}>{status}</Tag>;
                              },
                            },
                            {
                              title: 'Priority',
                              dataIndex: 'priority',
                              width: 80,
                              render: (priority: string) => {
                                const colorMap: Record<string, string> = {
                                  'Low': 'default',
                                  'Normal': 'blue',
                                  'High': 'orange',
                                  'Urgent': 'red',
                                };
                                return <Tag color={colorMap[priority]}>{priority}</Tag>;
                              },
                            },
                            {
                              title: 'Items',
                              width: 60,
                              render: (_: any, record: any) => record.items?.length || 0,
                            },
                            {
                              title: 'Est. Cost',
                              dataIndex: 'total_estimated_cost',
                              width: 100,
                              render: (cost: number | string) => {
                                const numCost = typeof cost === 'string' ? parseFloat(cost) : cost;
                                const n = numCost && Number.isFinite(numCost) ? numCost : 0;
                                return formatCurrency(n);
                              },
                            },
                            {
                              title: 'Request Date',
                              dataIndex: 'request_date',
                              width: 120,
                              render: (date: string) => date ? dayjs(date).format('DD MMM YYYY') : '-',
                            },
                            {
                              title: 'Actions',
                              width: 100,
                              render: (_: any, record: any) => (
                                <Button 
                                  type="link" 
                                  size="small"
                                  icon={<EyeOutlined />}
                                  onClick={() => {
                                    setSelectedMaterialRequestId(record.id);
                                    setIsMaterialRequestDetailVisible(true);
                                  }}
                                >
                                  View
                                </Button>
                              ),
                            },
                          ]}
                        />
                      ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                          <Text type="secondary">No material requests yet. Click "Request Materials" to create one.</Text>
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'lineItems',
                  label: `Line Items (${workOrderDetails.lineItems?.length || 0})`,
                  children: (
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <Table
                      dataSource={workOrderDetails.lineItems || []}
                      rowKey="id"
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: 'Line',
                          dataIndex: 'line_number',
                          width: 60,
                        },
                        {
                          title: 'Item Code',
                          dataIndex: 'item_code',
                          width: 120,
                        },
                        {
                          title: 'Description',
                          dataIndex: 'item_description',
                          ellipsis: true,
                        },
                        {
                          title: 'Operations',
                          key: 'ops_source',
                          width: 200,
                          render: (_: unknown, record: { id: string }) =>
                            lineItemOpsSourceSummary.get(record.id) ?? <Text type="secondary">—</Text>,
                        },
                        {
                          title: 'Line attachments',
                          key: 'boq_line_docs',
                          width: 300,
                          render: (_: unknown, record: { id: string }) => {
                            if (!canSeeWoBoqLineDocs) {
                              return (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  Needs manufacturing view
                                </Text>
                              );
                            }
                            const row = boqLineDocsByWoLineId.get(record.id);
                            const n = row?.attachments?.length ?? 0;
                            if (!row?.boq_line_item_id) {
                              return <Text type="secondary">No BOQ line link</Text>;
                            }
                            const sourceLabel =
                              row.boq_line_source === 'persisted'
                                ? 'WO snapshot'
                                : row.boq_line_source === 'resolved_from_quotation'
                                  ? 'Quotation / BOQ chain'
                                  : 'Estimation';
                            return (
                              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Space size={6} wrap align="center">
                                  <Tag color={n > 0 ? 'green' : 'default'}>
                                    {n} file{n === 1 ? '' : 's'}
                                  </Tag>
                                  <Text type="secondary" style={{ fontSize: 11 }}>
                                    {sourceLabel}
                                  </Text>
                                </Space>
                                {n > 0 ? (
                                  <Space direction="vertical" size={2} style={{ width: '100%' }}>
                                    {row.attachments.map((a) => {
                                      const label = a.file_name || a.document_name || 'Document';
                                      const cat = String(a.document_category ?? '').trim().toLowerCase();
                                      const catLabel =
                                        cat === 'technical' ? 'Technical' : cat === 'attachment' ? 'Attachments' : null;
                                      return (
                                        <div
                                          key={a.id}
                                          style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            width: '100%',
                                            minWidth: 0,
                                          }}
                                        >
                                          {catLabel ? (
                                            <Tag
                                              color={cat === 'technical' ? 'blue' : 'default'}
                                              style={{ marginInlineEnd: 0, flex: '0 0 auto' }}
                                            >
                                              {catLabel}
                                            </Tag>
                                          ) : null}
                                          <span
                                            title={label}
                                            style={{
                                              flex: '1 1 auto',
                                              minWidth: 0,
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                              fontSize: 12,
                                            }}
                                          >
                                            {label}
                                          </span>
                                          <span
                                            style={{
                                              flex: '0 0 auto',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: 2,
                                            }}
                                          >
                                            <Tooltip title="View">
                                              <Button
                                                type="link"
                                                size="small"
                                                icon={<EyeOutlined />}
                                                style={{ padding: 0 }}
                                                onClick={async () => {
                                                  try {
                                                    await documentService.openDocumentInNewTab(a.id);
                                                  } catch (err: unknown) {
                                                    const msg =
                                                      err instanceof Error && err.message
                                                        ? err.message
                                                        : 'Could not open document';
                                                    message.error(msg);
                                                  }
                                                }}
                                              />
                                            </Tooltip>
                                            <Tooltip title="Download">
                                              <Button
                                                type="link"
                                                size="small"
                                                icon={<DownloadOutlined />}
                                                style={{ padding: 0 }}
                                                onClick={async () => {
                                                  try {
                                                    const blob =
                                                      await documentService.downloadDocument(a.id);
                                                    if (blob.type?.includes('application/json')) {
                                                      const text = await blob.text();
                                                      let msg = 'Download failed';
                                                      try {
                                                        const parsed = JSON.parse(text) as {
                                                          message?: string;
                                                        };
                                                        if (parsed?.message) msg = parsed.message;
                                                      } catch {
                                                        /* ignore */
                                                      }
                                                      message.error(msg);
                                                      return;
                                                    }
                                                    const url = window.URL.createObjectURL(blob);
                                                    const el = document.createElement('a');
                                                    el.href = url;
                                                    el.download = label;
                                                    el.click();
                                                    window.setTimeout(
                                                      () => window.URL.revokeObjectURL(url),
                                                      2000
                                                    );
                                                  } catch {
                                                    message.error('Download failed');
                                                  }
                                                }}
                                              />
                                            </Tooltip>
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </Space>
                                ) : (
                                  <Text type="secondary" style={{ fontSize: 11 }}>
                                    None on BOQ line
                                  </Text>
                                )}
                              </Space>
                            );
                          },
                        },
                        {
                          title: 'Heat Number',
                          dataIndex: 'heat_number',
                          width: 120,
                          render: (text: string) => text || <Text type="secondary">-</Text>,
                        },
                        {
                          title: 'Planned Qty',
                          dataIndex: 'planned_quantity',
                          width: 120,
                          render: (qty: number, record: any) => `${qty} ${record.uom}`,
                        },
                        {
                          title: 'Produced Qty',
                          dataIndex: 'produced_quantity',
                          width: 120,
                          render: (qty: number, record: any) => `${qty} ${record.uom}`,
                        },
                        {
                          title: 'Progress',
                          width: 120,
                          render: (_: any, record: any) => (
                            <Progress
                              percent={Math.round((Number(record.produced_quantity) / Number(record.planned_quantity)) * 100)}
                              size="small"
                              status={Number(record.produced_quantity) >= Number(record.planned_quantity) ? 'success' : 'active'}
                            />
                          ),
                        },
                      ]}
                    />
                    </Space>
                  ),
                },
                {
                  key: 'fgReceipts',
                  label: `Finished goods (${fgReceipts.length})`,
                  children: (
                    <div style={{ minHeight: 120 }}>
                      {loadingFgReceipts ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                          <Spin />
                        </div>
                      ) : fgReceipts.length > 0 ? (
                        <Table<ProductionReceiptRow>
                          dataSource={fgReceipts}
                          rowKey="id"
                          pagination={false}
                          size="small"
                          columns={[
                            { title: 'Receipt #', dataIndex: 'receipt_number', width: 120 },
                            {
                              title: 'Date',
                              dataIndex: 'receipt_date',
                              width: 110,
                              render: (d: string) => (d ? String(d).slice(0, 10) : '—'),
                            },
                            {
                              title: 'Item',
                              key: 'item',
                              render: (_: unknown, r: ProductionReceiptRow) =>
                                r.item?.item_code || r.item?.item_name || '—',
                            },
                            {
                              title: 'Produced',
                              dataIndex: 'produced_quantity',
                              width: 100,
                              render: (q: number, r: ProductionReceiptRow) =>
                                `${q} ${r.uom ?? ''}`.trim(),
                            },
                            {
                              title: 'Accepted',
                              dataIndex: 'accepted_quantity',
                              width: 90,
                            },
                            {
                              title: 'Rejected',
                              dataIndex: 'rejected_quantity',
                              width: 90,
                            },
                            {
                              title: 'Job schedule',
                              key: 'job',
                              width: 140,
                              render: (_: unknown, r: ProductionReceiptRow) =>
                                r.jobSchedule?.schedule_number || r.job_schedule_id || '—',
                            },
                            {
                              title: 'Posted',
                              dataIndex: 'is_posted',
                              width: 80,
                              render: (posted: boolean) =>
                                posted ? <Tag color="success">Yes</Tag> : <Tag>No</Tag>,
                            },
                          ]}
                        />
                      ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                          <Text type="secondary">
                            No finished goods receipts yet. Receipts are created when a job schedule is completed with
                            produced quantity.
                          </Text>
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'documents',
                  label: `Documents (${workOrderDetails.documents?.length || 0})`,
                  children: (
                    <div style={{ minHeight: 200 }}>
                      <Space wrap style={{ marginBottom: 12 }}>
                        <Button
                          type="primary"
                          icon={<SyncOutlined />}
                          disabled={!workOrderDetails.sales_order_id}
                          loading={syncingDocs}
                          onClick={async () => {
                            if (!selectedWorkOrderId) return;
                            setSyncingDocs(true);
                            try {
                              const r = await workOrderService.syncSalesOrderDocuments(selectedWorkOrderId);
                              message.success(
                                r.copied > 0
                                  ? `Copied ${r.copied} document(s) from the customer order`
                                  : 'No new documents to copy from the customer order'
                              );
                              queryClient.invalidateQueries({ queryKey: ['work-orders', selectedWorkOrderId] });
                            } catch (e: unknown) {
                              const msg =
                                e && typeof e === 'object' && 'message' in e
                                  ? String((e as { message: string }).message)
                                  : 'Sync failed';
                              message.error(msg);
                            } finally {
                              setSyncingDocs(false);
                            }
                          }}
                        >
                          Sync documents from customer order
                        </Button>
                        {!workOrderDetails.sales_order_id ? (
                          <Text type="secondary">Link a customer order on this WO to enable sync.</Text>
                        ) : null}
                      </Space>
                      {workOrderDetails.documents && workOrderDetails.documents.length > 0 ? (
                        <Table
                          dataSource={workOrderDetails.documents}
                          rowKey="id"
                          pagination={false}
                          size="small"
                          columns={[
                            {
                              title: 'Type',
                              dataIndex: 'document_type',
                              width: 100,
                              render: (type: string) => (
                                <Tag color="blue">{type}</Tag>
                              ),
                            },
                            {
                              title: 'Document Name',
                              dataIndex: 'document_name',
                            },
                            {
                              title: 'Uploaded',
                              dataIndex: 'uploaded_at',
                              width: 150,
                              render: (date: string) => date ? dayjs(date).format('DD MMM YYYY HH:mm') : '-',
                            },
                            {
                              title: 'Actions',
                              width: 100,
                              render: (_: any, record: any) => (
                                <Button 
                                  type="link" 
                                  size="small"
                                  onClick={() => {
                                    // Implement download functionality
                                    message.info('Document download functionality to be implemented');
                                  }}
                                >
                                  Download
                                </Button>
                              ),
                            },
                          ]}
                        />
                      ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                          <Text type="secondary">No documents attached</Text>
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'amendments',
                  label: `SO revisions (${soAmendments.length})`,
                  children: (
                    <div style={{ minHeight: 120 }}>
                      {!workOrderDetails.sales_order_id ? (
                        <Text type="secondary">This work order is not linked to a customer order.</Text>
                      ) : loadingSoAmendments ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                          <Spin />
                        </div>
                      ) : soAmendments.length > 0 ? (
                        <Table
                          rowKey="id"
                          dataSource={soAmendments}
                          pagination={false}
                          size="small"
                          columns={[
                            {
                              title: 'Changed at',
                              dataIndex: 'amended_at',
                              width: 170,
                              render: (v: string) => (v ? dayjs(v).format('DD MMM YYYY HH:mm') : '-'),
                            },
                            {
                              title: 'Reason',
                              dataIndex: 'reason',
                            },
                            {
                              title: 'Workload impact',
                              dataIndex: 'workload_impact_note',
                              render: (v: string | null) => v || '—',
                            },
                          ]}
                        />
                      ) : (
                        <Text type="secondary">
                          No sales-order revisions recorded for this work order yet.
                        </Text>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'assets',
                  label: `Assets / tools (${assetMovements.length})`,
                  children: (
                    <div style={{ minHeight: 120 }}>
                      <div style={{ marginBottom: 12 }}>
                        <Button
                          type="primary"
                          icon={<ToolOutlined />}
                          onClick={() => {
                            assetForm.resetFields();
                            assetForm.setFieldsValue({ movement_type: 'issue', quantity: 1 });
                            setAssetModalOpen(true);
                          }}
                        >
                          Record issue / return / damage
                        </Button>
                      </div>
                      {loadingAssetMovements ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                          <Spin />
                        </div>
                      ) : assetMovements.length > 0 ? (
                        <Table
                          rowKey="id"
                          dataSource={assetMovements}
                          pagination={false}
                          size="small"
                          columns={[
                            {
                              title: 'Type',
                              dataIndex: 'movement_type',
                              width: 110,
                              render: (v: string) => (
                                <Tag color={v === 'damage' ? 'red' : v === 'return' ? 'green' : 'blue'}>
                                  {v}
                                </Tag>
                              ),
                            },
                            {
                              title: 'Asset / tool',
                              render: (_: unknown, r: any) =>
                                r.asset_code ? `${r.asset_code} - ${r.asset_name || ''}` : r.asset_description,
                            },
                            {
                              title: 'Issued to',
                              dataIndex: 'issued_to_employee_id',
                              width: 220,
                              render: (v: string | null, row: any) => {
                                if (row.issued_to_employee_name) return row.issued_to_employee_name;
                                if (!v) return '—';
                                const u = activeUsers.find((x) => x.id === v);
                                return u ? `${u.first_name} ${u.last_name || ''}`.trim() : 'Unknown employee';
                              },
                            },
                            {
                              title: 'Qty',
                              width: 100,
                              render: (_: unknown, r: { quantity: number; uom?: string | null }) =>
                                `${r.quantity}${r.uom ? ` ${r.uom}` : ''}`,
                            },
                            {
                              title: 'Notes',
                              dataIndex: 'notes',
                              render: (v: string | null) => v || '—',
                            },
                            {
                              title: 'Time',
                              dataIndex: 'created_at',
                              width: 170,
                              render: (v: string) => (v ? dayjs(v).format('DD MMM YYYY HH:mm') : '-'),
                            },
                          ]}
                        />
                      ) : (
                        <Text type="secondary">No asset/tool movements recorded yet.</Text>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'inspections',
                  label: `Inspections (${inspections.length})`,
                  children: (
                    <div>
                      {isLoadingInspections ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                          <Spin />
                        </div>
                      ) : inspections.length > 0 ? (
                        <Table<StageInspectionTableRow>
                          dataSource={inspections as StageInspectionTableRow[]}
                          rowKey="id"
                          pagination={false}
                          size="small"
                          columns={[
                            {
                              title: 'Inspection #',
                              dataIndex: 'inspection_number',
                              width: 120,
                            },
                            {
                              title: 'Stage',
                              key: 'stage',
                              width: 120,
                              render: (_: unknown, record: StageInspectionTableRow) =>
                                record.workOrderStage?.stage_code ?? record.workOrderStage?.stage_name ?? '-',
                            },
                            {
                              title: 'Status',
                              dataIndex: 'status',
                              width: 120,
                              render: (status: string) => <Tag color={getInspectionStatusColor(status)}>{status}</Tag>,
                            },
                            {
                              title: 'Result',
                              dataIndex: 'result',
                              width: 100,
                              render: (result: string) => <Tag color={getResultColor(result)}>{result}</Tag>,
                            },
                            {
                              title: 'Inspection Date',
                              dataIndex: 'inspection_date',
                              width: 120,
                              render: (date: string) => (date ? dayjs(date).format('DD MMM YYYY') : '-'),
                            },
                            {
                              title: 'Inspector',
                              key: 'inspector',
                              width: 120,
                              ellipsis: true,
                              render: (_: unknown, record: StageInspectionTableRow) =>
                                record.inspector?.name ?? record.inspector?.email ?? '-',
                            },
                            {
                              title: 'Actions',
                              width: 100,
                              render: (_: unknown, record: StageInspectionTableRow) => (
                                <Button
                                  type="link"
                                  size="small"
                                  onClick={() => navigate(`/qms/inspections/${record.id}`)}
                                >
                                  View/Record
                                </Button>
                              ),
                            },
                          ]}
                        />
                      ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                          <Text type="secondary">
                            {workOrderDetails?.work_order_status === 'Inspection Pending'
                              ? 'No inspections created yet'
                              : 'Complete production steps and inspections as per your quality plan; line operations follow the quotation BOQ where linked, otherwise work order stages.'}
                          </Text>
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'qualityPlan',
                  label: (
                    <span>
                      Quality Plan
                      {qualityPlan && (
                        <Tag color={getQualityPlanStatusColor(qualityPlan.status)} style={{ marginLeft: 8 }}>
                          {qualityPlan.status}
                        </Tag>
                      )}
                    </span>
                  ),
                  children: (
                    <div>
                      {isLoadingQualityPlan ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                          <Spin />
                        </div>
                      ) : qualityPlan ? (
                        <Card>
                          <Descriptions bordered column={2} size="small">
                            <Descriptions.Item label="Plan Number">{qualityPlan.plan_number}</Descriptions.Item>
                            <Descriptions.Item label="Status">
                              <Tag color={getQualityPlanStatusColor(qualityPlan.status)}>{qualityPlan.status}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label="Version">{qualityPlan.version}</Descriptions.Item>
                            <Descriptions.Item label="Revision">{qualityPlan.revision_number}</Descriptions.Item>
                            {qualityPlan.effective_date && (
                              <Descriptions.Item label="Effective Date">
                                {dayjs(qualityPlan.effective_date).format('DD MMM YYYY')}
                              </Descriptions.Item>
                            )}
                            {qualityPlan.approval_date && (
                              <Descriptions.Item label="Approval Date">
                                {dayjs(qualityPlan.approval_date).format('DD MMM YYYY')}
                              </Descriptions.Item>
                            )}
                            {qualityPlan.items && (
                              <Descriptions.Item label="Inspection Points" span={2}>
                                {qualityPlan.items.length} point(s) defined
                              </Descriptions.Item>
                            )}
                          </Descriptions>
                          <Space style={{ marginTop: 16 }}>
                            <Button
                              type="primary"
                              onClick={() => navigate(`/qms/quality-plans/${qualityPlan.id}`)}
                            >
                              View Quality Plan Details
                            </Button>
                          </Space>
                        </Card>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                          <Text type="secondary">No quality plan created for this work order yet.</Text>
                          {(workOrderDetails.work_order_status === 'Draft' || workOrderDetails.work_order_status === 'Released') && (
                            <>
                              <br /><br />
                              <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setIsQualityPlanDialogVisible(true)}
                              >
                                Create Quality Plan
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'iir',
                  label: 'Job Inspection',
                  children: selectedWorkOrderId ? (
                    <InitialInspectionRequestComponent workOrderId={selectedWorkOrderId} />
                  ) : null,
                },
                {
                  key: 'operations',
                  label: 'Operations',
                  children:
                    selectedWorkOrderId && workOrderDetails ? (
                      <WorkOrderOperations
                        workOrderId={selectedWorkOrderId}
                        workOrderNumber={workOrderDetails.work_order_number}
                      />
                    ) : null,
                },
                {
                  key: 'fir',
                  label: 'Final Inspection (FIR)',
                  children: selectedWorkOrderId ? (
                    <FinalInspectionRequestComponent workOrderId={selectedWorkOrderId} />
                  ) : null,
                },
                {
                  key: 'irn',
                  label: 'Inspection Release Note (IRN)',
                  children: selectedWorkOrderId ? (
                    <Alert
                      type="info"
                      showIcon
                      message="IRN is on the delivery note"
                      description="Create and approve an Inspection Release Note from each delivery note that ships this work order. Open Sales → Delivery notes, select the note, then use the Quality / IRN section before marking the shipment in transit."
                    />
                  ) : null,
                },
              ]
                .filter(({ key }) => !['amendments', 'qualityPlan', 'fgReceipts'].includes(key))
                .sort((a, b) => (a.key === 'iir' ? -1 : b.key === 'iir' ? 1 : 0))}
            />
          </>
        )}
      </Card>
      )}

      <Modal
        title="Record tool / asset movement"
        open={assetModalOpen}
        onCancel={() => setAssetModalOpen(false)}
        onOk={async () => {
          try {
            const v = await assetForm.validateFields();
            if (!selectedWorkOrderId) return;
            setAssetSubmitting(true);
            const assetId = String(v.asset_id ?? '').trim();
            const movementPayload = {
              work_order_id: selectedWorkOrderId,
              to_employee_id: v.to_employee_id ?? null,
              remarks: v.notes?.trim() ? v.notes.trim() : null,
            };
            if (v.movement_type === 'issue') {
              await assetService.issueMovement(assetId, movementPayload);
            } else if (v.movement_type === 'return') {
              await assetService.returnMovement(assetId, movementPayload);
            } else {
              await assetService.damageMovement(assetId, movementPayload);
            }
            message.success('Asset movement recorded');
            setAssetModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ['work-order-asset-movements', selectedWorkOrderId] });
          } catch (e: unknown) {
            if (e && typeof e === 'object' && 'errorFields' in e) return;
            const msg =
              e && typeof e === 'object' && 'message' in e
                ? String((e as { message: string }).message)
                : 'Failed to record asset movement';
            message.error(msg);
          } finally {
            setAssetSubmitting(false);
          }
        }}
        confirmLoading={assetSubmitting}
        destroyOnHidden
      >
        <Form form={assetForm} layout="vertical">
          <Form.Item name="movement_type" label="Movement type" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'issue', label: 'Issue' },
                { value: 'return', label: 'Return' },
                { value: 'damage', label: 'Damage' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="asset_id"
            label="Asset / tool"
            rules={[{ required: true, message: 'Select asset/tool from master' }]}
          >
            <Select
              showSearch
              loading={loadingAvailableAssets}
              placeholder="Select asset from master"
              optionFilterProp="label"
              options={availableAssets.map((a) => ({
                value: a.id,
                label: `${a.asset_code} - ${a.asset_name}`,
              }))}
            />
          </Form.Item>
          {assetMovementType === 'issue' ? (
            <Form.Item
              name="to_employee_id"
              label="Issued to"
              rules={[{ required: true, message: 'Select employee for issue movement' }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                loading={usersLoading}
                placeholder="Select employee"
                options={activeUsers.map((u) => ({
                  value: u.id,
                  label: `${u.first_name} ${u.last_name || ''} (${u.email})`.trim(),
                }))}
              />
            </Form.Item>
          ) : null}
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={3} placeholder="Condition, return details, damage summary, etc." />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit execution and material utilization"
        open={execEditOpen}
        onCancel={() => setExecEditOpen(false)}
        width={880}
        destroyOnHidden
        confirmLoading={updateWorkOrderMutation.isPending}
        onOk={async () => {
          try {
            const v = await execForm.validateFields();
            if (!selectedWorkOrderId) return;
            const matErr = (() => {
              const rows = (v.matLines || []) as MaterialUtilExecFormRow[];
              const hasLineItems = (workOrderDetails?.lineItems?.length || 0) > 0;
              for (let i = 0; i < rows.length; i++) {
                const r = rows[i];
                const t = (s?: string) => (s ?? '').trim();
                const has =
                  t(r.drawingLineNo) ||
                  t(r.clientMaterialSpecification) ||
                  t(r.materialGrade) ||
                  t(r.headNo) ||
                  t(r.remarks) ||
                  t(r.workOrderLineItemId || undefined);
                if (!has) continue;
                if (hasLineItems && !t(r.workOrderLineItemId || undefined)) {
                  return `Material row ${i + 1}: Select the BOQ line item.`;
                }
                if (!t(r.drawingLineNo)) {
                  return `Material row ${i + 1}: Drawing line no. is required.`;
                }
                if (r.verification !== 'verified' && r.verification !== 'not_verified') {
                  return `Material row ${i + 1}: Select Verified or Not verified.`;
                }
              }
              return null;
            })();
            if (matErr) {
              modal.error({ title: 'Material utilization', content: matErr });
              return;
            }
            await updateWorkOrderMutation.mutateAsync({
              id: selectedWorkOrderId,
              data: {
                executionScope: v.executionScope,
                deviationType: v.deviationType,
                clientScopeOfWork: v.clientScopeOfWork?.trim() || null,
                clientQualityRequirements: v.clientQualityRequirements?.trim() || null,
                wo_customer_requirements_notes: v.wo_customer_requirements_notes?.trim() || null,
                productionSupervisorUserId: v.productionSupervisorUserId,
                qualityEngineerUserId: v.qualityEngineerUserId,
                materialUtilizationLines: mapMatFormToPayload(v.matLines as MaterialUtilExecFormRow[]),
              },
            });
            queryClient.invalidateQueries({ queryKey: ['work-orders', selectedWorkOrderId] });
            setExecEditOpen(false);
          } catch (e) {
            console.error(e);
          }
        }}
      >
        <Form form={execForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Scope" name="executionScope" rules={[{ required: true }]}>
                <Select options={WORK_ORDER_EXECUTION_SCOPES.map((x) => ({ value: x, label: x }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Deviation" name="deviationType" rules={[{ required: true }]}>
                <Select options={WORK_ORDER_DEVIATION_TYPES.map((x) => ({ value: x, label: x }))} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Scope of work (client)" name="clientScopeOfWork">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item label="Quality requirements" name="clientQualityRequirements">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item
            label="WO customer requirements notes"
            name="wo_customer_requirements_notes"
            tooltip="Copied from the customer order at WO creation; edit here for shop-floor divergence (NM_WP_70)."
          >
            <Input.TextArea rows={2} placeholder="Optional — WO-specific customer requirement notes" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Production supervisor" name="productionSupervisorUserId" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  loading={usersLoading}
                  options={activeUsers.map((u) => ({
                    value: u.id,
                    label: `${u.first_name} ${u.last_name || ''} (${u.email})`.trim(),
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Quality engineer" name="qualityEngineerUserId" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  loading={usersLoading}
                  options={activeUsers.map((u) => ({
                    value: u.id,
                    label: `${u.first_name} ${u.last_name || ''} (${u.email})`.trim(),
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Divider orientation="left">Material utilization</Divider>
          <Form.List name="matLines">
            {(fields, { add, remove }) => (
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                {fields.map(({ key, name, ...restField }) => (
                  <Card key={key} size="small" type="inner">
                    <Row gutter={8}>
                      {(workOrderDetails?.lineItems?.length || 0) > 0 ? (
                        <Col span={24}>
                          <Form.Item
                            {...restField}
                            label="BOQ line item"
                            name={[name, 'workOrderLineItemId']}
                            rules={[{ required: true, message: 'Select BOQ line item' }]}
                          >
                            <Select
                              showSearch
                              optionFilterProp="label"
                              placeholder="Select BOQ / WO line"
                              options={(workOrderDetails?.lineItems || []).map((li) => ({
                                value: li.id,
                                label: `L${li.line_number}${li.item_code ? ` · ${li.item_code}` : ''} — ${
                                  li.item_description || li.item_name || 'Line'
                                }`,
                              }))}
                            />
                          </Form.Item>
                        </Col>
                      ) : null}
                      <Col span={4}>
                        <Form.Item {...restField} label="Drawing line" name={[name, 'drawingLineNo']}>
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={5}>
                        <Form.Item
                          {...restField}
                          label="Client spec."
                          name={[name, 'clientMaterialSpecification']}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item {...restField} label="Grade" name={[name, 'materialGrade']}>
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item {...restField} label="Heat no." name={[name, 'headNo']}>
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={5}>
                        <Form.Item {...restField} label="Verification" name={[name, 'verification']}>
                          <Radio.Group>
                            <Radio value="verified">Verified</Radio>
                            <Radio value="not_verified">Not verified</Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                      <Col span={24}>
                        <Form.Item {...restField} label="Remarks" name={[name, 'remarks']}>
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col>
                        <MinusCircleOutlined
                          onClick={() => remove(name)}
                          style={{ color: '#ff4d4f', marginTop: 8 }}
                        />
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add material utilization row
                </Button>
              </Space>
            )}
          </Form.List>
        </Form>
      </Modal>

      {/* Material Request Detail Modal */}
      <MaterialRequestDetailModal
        visible={isMaterialRequestDetailVisible}
        materialRequestId={selectedMaterialRequestId}
        onClose={() => {
          setIsMaterialRequestDetailVisible(false);
          setSelectedMaterialRequestId(null);
        }}
        onIssue={
          canIssueMaterials
            ? (requestId) => {
                setIsMaterialRequestDetailVisible(false);
                setSelectedMaterialRequestId(null);
                navigate(`/scm/inventory/material-issues?material_request_id=${requestId}`);
              }
            : undefined
        }
      />

      {/* Create Quality Plan Dialog */}
      {selectedWorkOrderId && workOrderDetails && (
        <CreateQualityPlanDialog
          visible={isQualityPlanDialogVisible}
          workOrderId={selectedWorkOrderId}
          workOrderNumber={workOrderDetails.work_order_number}
          projectName={workOrderDetails.project?.project_name}
          onClose={() => setIsQualityPlanDialogVisible(false)}
          onSuccess={() => {
            setIsQualityPlanDialogVisible(false);
            // Invalidate and refetch quality plan for this work order
            queryClient.invalidateQueries({ queryKey: ['quality-plan-by-work-order', selectedWorkOrderId] });
            queryClient.invalidateQueries({ queryKey: ['work-orders', selectedWorkOrderId] });
            message.success('Quality plan created successfully');
          }}
        />
      )}
    </div>
  );
};

// Helper function for quality plan status colors
const getQualityPlanStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    'Draft': 'default',
    'Under Review': 'orange',
    'Approved': 'green',
    'Active': 'blue',
    'Superseded': 'default',
    'Cancelled': 'red',
  };
  return colors[status] || 'default';
};

export default WorkOrdersPage;


